import type { ZippyTheme } from '../types';

/** Aligned with zippy-kyc-web-sdk `src/styles/tokens.css` */
export const DEFAULT_THEME: Required<Omit<ZippyTheme, 'mode'>> & {
  mode: ZippyTheme['mode'];
  primaryGradientEnd: string;
  primaryHover: string;
  primarySoft: string;
  surfaceMuted: string;
  radiusButton: string;
  radiusInput: string;
  radiusCard: string;
} = {
  primary: '#3f63f3',
  primaryForeground: '#ffffff',
  primaryGradientEnd: '#6b84ff',
  primaryHover: '#2f4fd1',
  primarySoft: '#eef2ff',
  background: '#ffffff',
  surface: '#ffffff',
  surfaceMuted: '#f6f8fb',
  text: '#0f172a',
  textMuted: '#64748b',
  border: '#e2e8f0',
  error: '#dc2626',
  success: '#059669',
  borderRadius: '20px',
  radiusButton: '24px',
  radiusInput: '12px',
  radiusCard: '20px',
  fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
  mode: 'light',
};

const DARK_THEME_OVERRIDES: Partial<ZippyTheme> = {
  background: '#0f172a',
  surface: '#1e293b',
  text: '#f8fafc',
  textMuted: '#94a3b8',
  border: '#334155',
};

export type ResolvedTheme = typeof DEFAULT_THEME;

export function resolveTheme(theme?: ZippyTheme): ResolvedTheme {
  const merged = { ...DEFAULT_THEME, ...theme };
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  const effectiveMode =
    merged.mode === 'auto' ? (prefersDark ? 'dark' : 'light') : (merged.mode ?? 'light');

  if (effectiveMode === 'dark') {
    return { ...merged, ...DARK_THEME_OVERRIDES, mode: effectiveMode };
  }

  return { ...merged, mode: effectiveMode };
}

export function applyThemeToHost(host: HTMLElement, theme?: ZippyTheme): void {
  const resolved = resolveTheme(theme);
  host.style.setProperty('--zippy-primary', resolved.primary);
  host.style.setProperty('--zippy-primary-foreground', resolved.primaryForeground);
  host.style.setProperty('--zippy-primary-gradient-end', resolved.primaryGradientEnd);
  host.style.setProperty('--zippy-primary-hover', resolved.primaryHover);
  host.style.setProperty('--zippy-primary-soft', resolved.primarySoft);
  host.style.setProperty('--zippy-background', resolved.background);
  host.style.setProperty('--zippy-surface', resolved.surface);
  host.style.setProperty('--zippy-surface-muted', resolved.surfaceMuted);
  host.style.setProperty('--zippy-text', resolved.text);
  host.style.setProperty('--zippy-text-muted', resolved.textMuted);
  host.style.setProperty('--zippy-border', resolved.border);
  host.style.setProperty('--zippy-error', resolved.error);
  host.style.setProperty('--zippy-success', resolved.success);
  host.style.setProperty('--zippy-radius', resolved.borderRadius);
  host.style.setProperty('--zippy-radius-button', resolved.radiusButton);
  host.style.setProperty('--zippy-radius-input', resolved.radiusInput);
  host.style.setProperty('--zippy-radius-card', resolved.radiusCard);
  host.style.setProperty('--zippy-font-family', resolved.fontFamily);
}

const DM_SANS_IMPORT =
  "@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');";

export const CHECKOUT_STYLES = `
${DM_SANS_IMPORT}

:host {
  display: block;
  font-family: var(--zippy-font-family);
  color: var(--zippy-text);
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

*, *::before, *::after {
  box-sizing: border-box;
}

button,
input {
  font-family: inherit;
}

.zippy-root {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--zippy-background);
  border: 1px solid var(--zippy-border);
  border-radius: var(--zippy-radius-card);
  padding: 0;
  max-width: 420px;
  width: 100%;
  overflow: hidden;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 16px 48px -20px rgba(15, 23, 42, 0.18);
}

.zippy-root--terminal {
  display: block;
  padding: 32px 24px 28px;
  overflow: visible;
}

.zippy-root--paying {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.zippy-root-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 24px 24px 20px;
}

.zippy-payment-hero {
  margin-bottom: 22px;
  padding-bottom: 20px;
  text-align: center;
  border-bottom: 1px solid color-mix(in srgb, var(--zippy-border) 80%, transparent);
}

.zippy-payment-merchant {
  margin: 0 0 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--zippy-text-muted);
  line-height: 1.35;
}

.zippy-payment-amount {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1.05;
  color: var(--zippy-text);
}

.zippy-payment-subtitle {
  margin: 10px 0 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--zippy-primary);
  letter-spacing: -0.01em;
}

.zippy-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  margin: 0 0 2px;
  padding: 0.375rem 0.625rem 0.375rem 0.375rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--zippy-text-muted);
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition:
    color 150ms ease,
    background-color 150ms ease;
}

.zippy-back-btn:hover:not(:disabled) {
  color: var(--zippy-text);
  background: color-mix(in srgb, var(--zippy-text-muted) 10%, transparent);
}

.zippy-back-btn:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-back-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.zippy-back-btn-icon {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
}

.zippy-choose {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
}

.zippy-choose-mobile-app {
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: 2px;
}

.zippy-choose-mobile-app .zippy-mobile-app-pay-btn {
  max-width: none;
}

.zippy-choose-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.zippy-method-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 7.5rem;
  padding: 16px 10px;
  border: 1px solid var(--zippy-border);
  border-radius: 16px;
  background: var(--zippy-surface-muted);
  color: var(--zippy-text);
  cursor: pointer;
  text-align: center;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease,
    background 160ms ease;
}

.zippy-method-card:hover {
  border-color: color-mix(in srgb, var(--zippy-primary) 40%, var(--zippy-border));
  background: color-mix(in srgb, var(--zippy-primary-soft) 55%, white);
  box-shadow: 0 10px 28px -16px rgba(63, 99, 243, 0.45);
  transform: translateY(-1px);
}

.zippy-method-card:active {
  transform: translateY(0) scale(0.985);
}

.zippy-method-card:focus-visible {
  outline: 2px solid var(--zippy-primary);
  outline-offset: 2px;
}

.zippy-method-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: var(--zippy-primary);
  background: #ffffff;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--zippy-primary) 12%, var(--zippy-border));
}

.zippy-method-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.zippy-method-title {
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.zippy-method-hint {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--zippy-text-muted);
  line-height: 1.35;
}

.zippy-flow {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.zippy-flow--centered {
  align-items: center;
  text-align: center;
}

.zippy-flow-awaiting {
  align-items: center;
  text-align: center;
  padding: 8px 0 4px;
}

.zippy-flow-awaiting .zippy-identity-card {
  width: 100%;
  text-align: left;
}

.zippy-pay-progress {
  flex-shrink: 0;
  width: 100%;
  height: 2px;
  margin: 0;
  border-radius: 0;
  background: color-mix(in srgb, var(--zippy-border) 55%, transparent);
  overflow: hidden;
}

.zippy-root--qr .zippy-pay-progress {
  height: 4px;
}

.zippy-pay-progress-fill {
  display: block;
  width: 100%;
  height: 100%;
  transform: scaleX(1);
  transform-origin: left center;
  background: var(--zippy-primary);
  transition: transform 1s linear;
  will-change: transform;
}

.zippy-flow-hint {
  width: 100%;
  max-width: 18rem;
  margin: 0 auto;
  padding: 0 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 2px 0 4px;
}

.zippy-mobile-app-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 4px 0 8px;
}

.zippy-mobile-app-section .zippy-pay-timer-slot {
  margin: 0;
}

.zippy-mobile-app-pay-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 18rem;
  min-height: 3rem;
  padding: 0.75rem 1.25rem;
  border: 0;
  border-radius: 9999px;
  background: var(--zippy-primary);
  color: var(--zippy-primary-foreground);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    opacity 150ms ease,
    transform 150ms ease;
}

.zippy-mobile-app-pay-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--zippy-primary) 88%, #000);
}

.zippy-mobile-app-pay-btn:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-mobile-app-pay-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.zippy-mobile-app-pay-btn--opening {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  cursor: wait;
}

.zippy-mobile-app-pay-btn--opening:disabled {
  opacity: 1;
}

.zippy-mobile-app-pay-btn--opening .zippy-mobile-app-pay-btn-spinner {
  width: 1rem;
  height: 1rem;
  border-color: color-mix(in srgb, var(--zippy-primary-foreground) 35%, transparent);
  border-top-color: var(--zippy-primary-foreground);
}

.zippy-mobile-app-opened-hint {
  margin: 0;
  max-width: 18rem;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-mobile-app-method-switching {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
}

.zippy-mobile-app-method-switching-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-mobile-app-handoff-failed {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 4px 0;
}

.zippy-mobile-app-handoff-message {
  margin: 0;
  max-width: 20rem;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: var(--zippy-text);
  text-align: center;
}

.zippy-mobile-app-handoff-failed .zippy-install-zippy-btn {
  margin-top: 6px;
}

.zippy-install-zippy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 18rem;
  min-height: 3rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--zippy-border);
  border-radius: var(--zippy-radius-button);
  background: var(--zippy-surface);
  color: var(--zippy-text);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    border-color 150ms ease;
}

.zippy-install-zippy-btn:hover {
  background: color-mix(in srgb, var(--zippy-surface) 92%, var(--zippy-text));
}

.zippy-install-zippy-btn:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-mobile-app-fallback {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
}

.zippy-mobile-app-fallback-label {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-choose-options--compact {
  gap: 10px;
}

.zippy-mobile-app-retry {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--zippy-primary);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.zippy-mobile-app-retry:hover {
  color: color-mix(in srgb, var(--zippy-primary) 82%, #000);
}

.zippy-mobile-app-retry:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-pay-timer-slot {
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 3.5rem;
  margin: -6px 0 10px;
}

.zippy-qr-section .zippy-pay-timer-slot {
  margin: 0;
}

.zippy-pay-timer {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 6.75rem;
  padding: 0.5625rem 1.25rem 0.5rem;
  border-radius: 16px;
  background: var(--zippy-primary-soft);
  border: 1px solid color-mix(in srgb, var(--zippy-primary) 14%, transparent);
  color: var(--zippy-primary);
}

.zippy-pay-timer-label {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0;
  white-space: nowrap;
  color: color-mix(in srgb, var(--zippy-primary) 72%, var(--zippy-text-muted));
}

.zippy-pay-timer-value {
  font-size: 1.25rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  text-align: center;
  line-height: 1;
  white-space: nowrap;
}

.zippy-qr-frame {
  display: inline-block;
  padding: 16px;
  background: #ffffff;
  border: 1px solid var(--zippy-border);
  border-radius: 20px;
  line-height: 0;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 32px -14px rgba(63, 99, 243, 0.22);
}

.zippy-qr-stack {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.zippy-qr-stack > svg {
  display: block;
  width: 196px;
  height: 196px;
}

.zippy-qr-awaiting {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 4px;
}

.zippy-qr-awaiting-spinner {
  display: block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--zippy-border);
  border-top-color: var(--zippy-primary);
  border-radius: 50%;
  animation: zippy-spin 0.8s linear infinite;
}

.zippy-id-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
}

.zippy-input-combo {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 3rem;
  border: 1px solid var(--zippy-border);
  border-radius: var(--zippy-radius-button);
  background: var(--zippy-surface-muted);
  overflow: hidden;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease,
    background 150ms ease;
}

.zippy-input-combo:focus-within {
  border-color: var(--zippy-primary);
  background: var(--zippy-background);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--zippy-primary) 14%, transparent);
}

.zippy-input-combo--verified {
  border-color: color-mix(in srgb, var(--zippy-success) 35%, var(--zippy-border));
  background: color-mix(in srgb, var(--zippy-success) 6%, var(--zippy-background));
}

.zippy-input-combo--verified:focus-within {
  border-color: color-mix(in srgb, var(--zippy-success) 45%, var(--zippy-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--zippy-success) 12%, transparent);
}

.zippy-input-combo-input-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
}

.zippy-input-combo-field {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 0.75rem 0.875rem;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.25;
  color: var(--zippy-text);
  border: none;
  background: transparent;
  outline: none;
  transition: padding-right 150ms ease;
}

.zippy-input-combo-input-wrap--has-value .zippy-input-combo-field {
  padding-right: 2.375rem;
}

.zippy-input-combo-field::placeholder {
  color: var(--zippy-text-muted);
}

.zippy-input-combo-field:focus,
.zippy-input-combo-field:focus-visible {
  outline: none;
}

.zippy-input-combo-field:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.zippy-input-combo-clear {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin-top: auto;
  margin-bottom: auto;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--zippy-text-muted);
  cursor: pointer;
  appearance: none;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    background-color 150ms ease,
    color 150ms ease,
    opacity 150ms ease,
    visibility 150ms ease;
}

.zippy-input-combo-input-wrap--has-value .zippy-input-combo-clear {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.zippy-input-combo-clear:hover:not(:disabled) {
  background: color-mix(in srgb, var(--zippy-text-muted) 16%, transparent);
  color: var(--zippy-text);
}

.zippy-input-combo-clear:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 1px;
}

.zippy-input-combo-clear:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.zippy-input-combo-clear-icon {
  display: block;
  width: 0.875rem;
  height: 0.875rem;
  flex-shrink: 0;
}

.zippy-input-combo-divider {
  flex-shrink: 0;
  align-self: center;
  width: 1px;
  height: 1.375rem;
  background: var(--zippy-border);
}

.zippy-input-combo--verified .zippy-input-combo-divider {
  background: color-mix(in srgb, var(--zippy-success) 25%, var(--zippy-border));
}

.zippy-input-combo-action {
  flex-shrink: 0;
  min-width: 5.25rem;
  padding: 0 1rem;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--zippy-primary);
  background: transparent;
  border: none;
  cursor: pointer;
  transition:
    background 150ms ease,
    color 150ms ease,
    opacity 150ms ease;
}

.zippy-input-combo-action:hover:not(:disabled) {
  background: color-mix(in srgb, var(--zippy-primary) 8%, transparent);
}

.zippy-input-combo-action:disabled {
  color: var(--zippy-text-muted);
  cursor: default;
}

.zippy-input-combo--verified .zippy-input-combo-action:disabled {
  color: var(--zippy-success);
}

.zippy-input-combo-action--verified {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3rem;
  padding: 0 0.875rem;
  color: var(--zippy-success);
}

.zippy-input-combo-action--verified:disabled {
  opacity: 1;
}

.zippy-input-combo-check {
  display: block;
  width: 1.125rem;
  height: 1.125rem;
}

.zippy-input-combo-action--loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.zippy-input-combo-action--loading:disabled {
  opacity: 1;
  color: var(--zippy-primary);
}

.zippy-spinner-inline {
  width: 1rem;
  height: 1rem;
  border: 2px solid color-mix(in srgb, var(--zippy-primary) 18%, transparent);
  border-top-color: var(--zippy-primary);
  border-radius: 50%;
  animation: zippy-spin 0.7s linear infinite;
}

.zippy-input-combo--error {
  border-color: color-mix(in srgb, var(--zippy-error) 55%, var(--zippy-border));
  background: color-mix(in srgb, var(--zippy-error) 5%, var(--zippy-background));
}

.zippy-input-combo--error:focus-within {
  border-color: var(--zippy-error);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--zippy-error) 14%, transparent);
}

.zippy-input-combo--error .zippy-input-combo-divider {
  background: color-mix(in srgb, var(--zippy-error) 25%, var(--zippy-border));
}

.zippy-field-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: -4px 0 0;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--zippy-error);
  background: color-mix(in srgb, var(--zippy-error) 8%, var(--zippy-background));
  border: 1px solid color-mix(in srgb, var(--zippy-error) 18%, var(--zippy-border));
  border-radius: 999px;
}

.zippy-field-error-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  background: var(--zippy-error);
}

.zippy-field-hint {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--zippy-text-muted);
}

.zippy-query-hint {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: calc(var(--zippy-radius-input) + 2px);
  background: color-mix(in srgb, var(--zippy-text-muted) 5%, var(--zippy-surface-muted));
  border: 1px solid color-mix(in srgb, var(--zippy-border) 85%, transparent);
}

.zippy-query-hint-lead {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  color: var(--zippy-text-muted);
}

.zippy-query-hint-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.zippy-query-hint-item {
  display: grid;
  grid-template-columns: minmax(4.5rem, 5.25rem) minmax(0, 1fr);
  align-items: start;
  gap: 8px 10px;
}

.zippy-query-hint-label {
  padding-top: 0.1875rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--zippy-text-muted);
}

.zippy-query-hint-example-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
}

.zippy-query-hint-example {
  display: inline-block;
  max-width: 100%;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  background: var(--zippy-background);
  border: 1px solid color-mix(in srgb, var(--zippy-border) 90%, transparent);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  color: var(--zippy-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.zippy-query-hint-note {
  font-size: 0.6875rem;
  line-height: 1.35;
  color: var(--zippy-text-muted);
}

.zippy-picker-lead {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--zippy-text-muted);
}

.zippy-picker-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.zippy-picker-option {
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid color-mix(in srgb, var(--zippy-border) 90%, transparent);
  border-radius: calc(var(--zippy-radius-input) + 2px);
  background: var(--zippy-surface);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease,
    box-shadow 150ms ease;
}

.zippy-picker-option:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--zippy-primary) 35%, var(--zippy-border));
  background: color-mix(in srgb, var(--zippy-primary) 4%, var(--zippy-surface));
}

.zippy-picker-option:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-picker-option:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.zippy-picker-option .zippy-identity-card {
  margin: 0;
  border: 0;
  background: transparent;
}

.zippy-idle-hint {
  margin: -6px 0 0;
  font-size: 0.8125rem;
  color: var(--zippy-text-muted);
  text-align: center;
  line-height: 1.45;
}

.zippy-identity-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 16px;
  background: var(--zippy-surface-muted);
  border: 1px solid var(--zippy-border);
}

.zippy-identity-card--dismissible {
  position: relative;
  padding-right: 2.75rem;
}

.zippy-identity-card-dismiss {
  position: absolute;
  top: 50%;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--zippy-text-muted);
  cursor: pointer;
  appearance: none;
  transform: translateY(-50%);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.zippy-identity-card-dismiss:hover:not(:disabled) {
  background: color-mix(in srgb, var(--zippy-text-muted) 16%, transparent);
  color: var(--zippy-text);
}

.zippy-identity-card-dismiss:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-identity-card-dismiss:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.zippy-identity-card-dismiss-icon {
  display: block;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.zippy-identity-avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--zippy-primary);
  background: #fff;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--zippy-primary) 14%, var(--zippy-border));
}

.zippy-identity-avatar--photo {
  padding: 0;
  overflow: hidden;
  color: transparent;
  background: var(--zippy-surface-muted);
}

.zippy-identity-avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.zippy-identity-label {
  margin: 0 0 2px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--zippy-text-muted);
}

.zippy-identity-name {
  margin: 0 0 2px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--zippy-text);
  letter-spacing: -0.01em;
}

.zippy-identity-handle {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--zippy-text-muted);
}

.zippy-identity-chip {
  padding: 0.45rem 0.875rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--zippy-primary);
  background: var(--zippy-primary-soft);
}

.zippy-awaiting-status {
  padding: 4px 0 8px;
}

.zippy-awaiting-ring {
  position: relative;
  width: 56px;
  height: 56px;
  margin: 0 auto;
}

.zippy-awaiting-ring::before,
.zippy-awaiting-ring span {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
}

.zippy-awaiting-ring::before {
  border: 2px solid var(--zippy-primary-soft);
}

.zippy-awaiting-ring span {
  border: 2px solid transparent;
  border-top-color: var(--zippy-primary);
  animation: zippy-spin 0.9s linear infinite;
}

.zippy-awaiting-message {
  margin: 0;
  max-width: 18rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--zippy-text-muted);
}

.zippy-footnote {
  width: 100%;
  max-width: 18rem;
  margin: 0 auto;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-flow--centered .zippy-identity-card {
  width: 100%;
  text-align: left;
}

.zippy-brand-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 14px;
}

.zippy-root--terminal .zippy-brand-footer {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid color-mix(in srgb, var(--zippy-border) 80%, transparent);
}

.zippy-app-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.zippy-app-links-label {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--zippy-text-muted);
}

.zippy-app-links-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.zippy-app-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  flex: 1 1 8.5rem;
  max-width: 11rem;
  min-height: 2rem;
  padding: 0.375rem 0.625rem;
  border: 1px solid color-mix(in srgb, var(--zippy-border) 90%, transparent);
  border-radius: 999px;
  background: var(--zippy-surface);
  color: var(--zippy-text);
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  appearance: none;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;
}

.zippy-app-link--icon-only {
  flex: 0 0 auto;
  width: 2.375rem;
  min-width: 2.375rem;
  max-width: 2.375rem;
  height: 2.375rem;
  min-height: 2.375rem;
  padding: 0;
  border-radius: 999px;
}

.zippy-app-link--icon-only .zippy-app-link-icon {
  width: 1rem;
  height: 1rem;
}

.zippy-app-link--icon-only .zippy-app-link-icon--play {
  width: 0.9375rem;
  height: 1.0625rem;
}

.zippy-app-link:hover {
  border-color: color-mix(in srgb, var(--zippy-primary) 35%, var(--zippy-border));
  background: color-mix(in srgb, var(--zippy-primary) 6%, var(--zippy-surface));
  color: var(--zippy-text);
}

.zippy-app-link:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-app-link-icon {
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
}

.zippy-app-link-icon--play {
  width: 0.8125rem;
  height: 0.9375rem;
}

.zippy-app-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147482000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.58);
  animation: zippy-app-modal-fade 0.18s ease-out;
}

.zippy-app-modal-panel {
  position: relative;
  width: min(100%, 22rem);
  max-height: calc(100vh - 32px);
  overflow: auto;
  padding: 22px 20px 20px;
  border: 1px solid var(--zippy-border);
  border-radius: calc(var(--zippy-radius-card) - 2px);
  background: var(--zippy-background);
  color: var(--zippy-text);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.05),
    0 24px 56px -24px rgba(15, 23, 42, 0.35);
  animation: zippy-app-modal-in 0.2s ease-out;
}

.zippy-app-modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--zippy-text-muted);
  cursor: pointer;
  appearance: none;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.zippy-app-modal-close-icon {
  display: block;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.zippy-app-modal-close:hover {
  background: color-mix(in srgb, var(--zippy-text-muted) 16%, transparent);
  color: var(--zippy-text);
}

.zippy-app-modal-close:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-app-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding-right: 1.75rem;
}

.zippy-app-modal-platform-icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  margin-top: 2px;
  color: var(--zippy-text);
}

.zippy-app-modal-platform-icon--play {
  width: 1.75rem;
  height: 1.9375rem;
}

.zippy-app-modal-heading {
  min-width: 0;
}

.zippy-app-modal-title {
  margin: 0 0 6px;
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.zippy-app-modal-lead {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--zippy-text-muted);
}

.zippy-app-modal-qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
}

.zippy-app-modal-qr-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 12.5rem;
  aspect-ratio: 1;
  padding: 14px;
  border: 1px solid color-mix(in srgb, var(--zippy-border) 90%, transparent);
  border-radius: 16px;
  background: #ffffff;
}

.zippy-app-modal-qr-frame .zippy-qr-stack {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  line-height: 0;
}

.zippy-app-modal-qr-frame .zippy-qr-stack > svg {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

.zippy-app-modal-qr-hint {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-app-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.zippy-app-modal-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0.625rem 1rem;
  border-radius: 999px;
  background: var(--zippy-primary);
  color: var(--zippy-primary-foreground);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.zippy-app-modal-cta:hover {
  background: color-mix(in srgb, var(--zippy-primary) 88%, #000000);
  transform: translateY(-1px);
}

.zippy-app-modal-cta:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--zippy-primary) 45%, transparent);
  outline-offset: 2px;
}

.zippy-app-modal-mobile-note {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--zippy-text-muted);
  text-align: center;
}

.zippy-app-modal-overlay--mobile .zippy-app-modal-qr-wrap {
  opacity: 0.92;
}

@keyframes zippy-app-modal-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes zippy-app-modal-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.zippy-brand-footer-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 1.375rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  color: var(--zippy-text-muted);
  background: color-mix(in srgb, var(--zippy-text-muted) 6%, transparent);
  opacity: 0.72;
}

.zippy-brand-secure {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.zippy-brand-divider {
  flex-shrink: 0;
  align-self: center;
  width: 1px;
  height: 0.625rem;
  background: color-mix(in srgb, var(--zippy-text-muted) 24%, transparent);
}

.zippy-brand-lock {
  flex-shrink: 0;
  display: block;
  width: 0.625rem;
  height: 0.625rem;
}

.zippy-brand-copy,
.zippy-brand-name {
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
}

.zippy-brand-name {
  font-weight: 600;
}

.zippy-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0;
  color: var(--zippy-text);
}

.zippy-input {
  width: 100%;
  min-height: 2.875rem;
  padding: 0.6875rem 0.875rem;
  font-size: 1rem;
  border: 1px solid var(--zippy-border);
  border-radius: var(--zippy-radius-input);
  background: var(--zippy-background);
  color: var(--zippy-text);
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.zippy-input:focus {
  border-color: var(--zippy-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--zippy-primary) 16%, transparent);
}

.zippy-input:focus-visible {
  outline: 2px solid var(--zippy-primary);
  outline-offset: 2px;
}

.zippy-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.zippy-error-text {
  font-size: 0.8125rem;
  color: var(--zippy-error);
  margin: 0;
}

.zippy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.875rem;
  padding: 0.6875rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: var(--zippy-radius-button);
  border: none;
  cursor: pointer;
  transition:
    background 150ms ease,
    transform 150ms ease,
    box-shadow 150ms ease;
}

.zippy-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.zippy-btn:not(:disabled):active {
  transform: scale(0.985);
}

.zippy-btn-block {
  width: 100%;
}

.zippy-btn-primary {
  background: linear-gradient(
    135deg,
    var(--zippy-primary) 0%,
    var(--zippy-primary-gradient-end) 100%
  );
  color: var(--zippy-primary-foreground);
  box-shadow: 0 4px 14px -4px color-mix(in srgb, var(--zippy-primary) 45%, transparent);
}

.zippy-btn-primary:hover:not(:disabled) {
  background: linear-gradient(
    135deg,
    var(--zippy-primary-hover) 0%,
    var(--zippy-primary) 100%
  );
}

.zippy-status {
  text-align: center;
  padding: 16px 0 8px;
}

.zippy-status-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zippy-status-icon.loading {
  background: transparent;
  width: auto;
  height: auto;
  margin: 0 auto 14px;
}

.zippy-status-icon.success {
  background: color-mix(in srgb, var(--zippy-success) 14%, transparent);
  color: var(--zippy-success);
}

.zippy-status-icon.failure,
.zippy-status-icon.error {
  background: color-mix(in srgb, var(--zippy-error) 12%, transparent);
  color: var(--zippy-error);
}

.zippy-status-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: -0.02em;
}

.zippy-status-detail {
  font-size: 0.875rem;
  color: var(--zippy-text-muted);
  margin: 0;
  line-height: 1.5;
}

.zippy-transaction-ref {
  margin: 18px auto 0;
  max-width: 100%;
  padding: 12px 14px;
  border-radius: calc(var(--zippy-radius) * 0.75);
  background: var(--zippy-surface-muted);
  text-align: center;
}

.zippy-transaction-ref-label {
  margin: 0 0 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--zippy-text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.zippy-transaction-ref-value {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: var(--zippy-text);
  word-break: break-all;
}

.zippy-status-exit {
  margin: 20px 0 0;
  font-size: 0.875rem;
  color: var(--zippy-text-muted);
  line-height: 1.5;
}

.zippy-status-exit-countdown {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--zippy-text);
}

.zippy-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid var(--zippy-border);
  border-top-color: var(--zippy-primary);
  border-radius: 50%;
  animation: zippy-spin 0.8s linear infinite;
}

@keyframes zippy-spin {
  to { transform: rotate(360deg); }
}
`;

export const MODAL_STYLES = `
.zippy-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 2147483000;
}

.zippy-modal-panel {
  max-width: 460px;
  width: 100%;
  animation: zippy-modal-in 0.2s ease-out;
}

@keyframes zippy-modal-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
`;
